import React, { useState, useEffect, useCallback } from "react";
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getPlans, updatePlan, deletePlan, createPlan } from "../api/plans";
import debounce from "lodash/debounce";
import { useFormik } from "formik";
import * as Yup from "yup";

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [limit] = useState(10);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    description: Yup.string().required("Description is required"),
    type: Yup.string().required("Type is required"),
    price: Yup.number()
      .required("Price is required")
      .min(0, "Price must be positive"),
    trialEnabled: Yup.boolean(),
    trialDays: Yup.number().when("trialEnabled", {
      is: true,
      then: (schema) =>
        schema
          .required("Trial days is required when trial is enabled")
          .min(1, "Trial days must be at least 1"),
    }),
    allowedTemplateCreation: Yup.number()
      .min(0, "Template creation limit must be 0 or greater")
      .required("Template creation limit is required"),
    allowedSignRequests: Yup.number()
      .min(0, "Sign requests limit must be 0 or greater")
      .required("Sign requests limit is required"),
    allowBulkSend: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      type: "Monthly",
      price: "",
      trialEnabled: false,
      trialDays: 14,
      allowedTemplateCreation: 0,
      allowedSignRequests: 0,
      allowBulkSend: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedPlan) {
          // For updates, only send the fields that can be updated
          const updateData = {
            name: values.name,
            description: values.description,
            trialEnabled: values.trialEnabled,
            trialDays: values.trialDays,
            allowedTemplateCreation: values.allowedTemplateCreation,
            allowedSignRequests: values.allowedSignRequests,
            allowBulkSend: values.allowBulkSend,
            features: [],
          };
          await updatePlan(selectedPlan._id, updateData);
          toast.success("Plan updated successfully");
        } else {
          await createPlan(values);
          toast.success("Plan created successfully");
        }
        setIsEditModalOpen(false);
        setIsCreateModalOpen(false);
        setSelectedPlan(null);
        formik.resetForm();
        fetchPlans();
      } catch (error) {
        toast.error(error.message || "Operation failed");
      }
    },
  });
  console.log(formik);
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await getPlans({
        page: currentPage,
        limit,
        search,
        sortBy,
        sortOrder,
      });

      setPlans(response.data || []);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.total);
    } catch (error) {
      toast.error(error.message || "Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchPlans = useCallback(
    debounce(() => {
      fetchPlans();
    }, 500),
    [currentPage, search, sortBy, sortOrder]
  );

  useEffect(() => {
    debouncedFetchPlans();
    return () => {
      debouncedFetchPlans.cancel();
    };
  }, [currentPage, search, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    formik.setValues({
      name: plan.name || "",
      description: plan.description || "",
      type: plan.type || "Monthly",
      price: plan.price || "",
      trialEnabled: plan.trialEnabled || false,
      trialDays: plan.trialDays || 14,
      allowedTemplateCreation: plan.allowedTemplateCreation || 0,
      allowedSignRequests: plan.allowedSignRequests || 0,
      allowBulkSend: plan.allowBulkSend || false,
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (planId) => {
    if (window.confirm("Are you sure you want to delete this plan?")) {
      try {
        await deletePlan(planId);
        toast.success("Plan deleted successfully");
        fetchPlans();
      } catch (error) {
        toast.error(error.message || "Failed to delete plan");
      }
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUpIcon className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 inline-block ml-1" />
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Plans</h1>
        <button
          onClick={() => {
            setSelectedPlan(null);
            formik.resetForm();
            setIsCreateModalOpen(true);
          }}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Plan</span>
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search plans..."
            value={search}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  Name <SortIcon column="name" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trial
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template Limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sign Requests Limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bulk Send
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : plans.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No plans found
                  </td>
                </tr>
              ) : (
                plans.map((plan) => (
                  <tr key={plan._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {plan.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {plan.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{plan.type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">${plan.price}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {plan.trialEnabled ? (
                          <span className="text-green-600">
                            {plan.trialDays} days
                          </span>
                        ) : (
                          <span className="text-gray-500">No trial</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {plan.allowedTemplateCreation === 0 ? (
                          <span className="text-green-600">Unlimited</span>
                        ) : (
                          <span>{plan.allowedTemplateCreation}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {plan.allowedSignRequests === 0 ? (
                          <span className="text-green-600">Unlimited</span>
                        ) : (
                          <span>{plan.allowedSignRequests}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {plan.allowBulkSend ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{plan._id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(plan)}
                          className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          title="Edit Plan"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(plan._id)}
                          className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          title="Delete Plan"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * limit, totalItems)}
                </span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === index + 1
                        ? "z-10 bg-primary border-primary text-white"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={formik.handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          formik.errors.name && formik.touched.name
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <p className="mt-1 text-sm text-red-600">
                          {formik.errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        rows={3}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          formik.errors.description &&
                          formik.touched.description
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      {formik.touched.description &&
                        formik.errors.description && (
                          <p className="mt-1 text-sm text-red-600">
                            {formik.errors.description}
                          </p>
                        )}
                    </div>

                    {!selectedPlan && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="type"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Type
                          </label>
                          <select
                            id="type"
                            name="type"
                            value={formik.values.type}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                              formik.errors.type && formik.touched.type
                                ? "border-red-500"
                                : "border-gray-300"
                            } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                          >
                            <option value="Monthly">Monthly</option>
                            <option value="Yearly">Yearly</option>
                            <option value="One-time">One-time</option>
                          </select>
                          {formik.touched.type && formik.errors.type && (
                            <p className="mt-1 text-sm text-red-600">
                              {formik.errors.type}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="price"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Price
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="number"
                              id="price"
                              name="price"
                              value={formik.values.price}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              min="0"
                              step="0.01"
                              className={`py-3 pl-10 pr-4 block w-full rounded-md border-2 ${
                                formik.errors.price && formik.touched.price
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                            />
                          </div>
                          {formik.touched.price && formik.errors.price && (
                            <p className="mt-1 text-sm text-red-600">
                              {formik.errors.price}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Trial Settings */}
                    <div className="border-t pt-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Trial Settings
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="trialEnabled"
                            name="trialEnabled"
                            checked={formik.values.trialEnabled}
                            onChange={formik.handleChange}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label
                            htmlFor="trialEnabled"
                            className="ml-2 block text-sm text-gray-900"
                          >
                            Enable Trial
                          </label>
                        </div>

                        {formik.values.trialEnabled && (
                          <div>
                            <label
                              htmlFor="trialDays"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Trial Days
                            </label>
                            <input
                              type="number"
                              id="trialDays"
                              name="trialDays"
                              value={formik.values.trialDays}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              min="1"
                              className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                                formik.errors.trialDays &&
                                formik.touched.trialDays
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                            />
                            {formik.touched.trialDays &&
                              formik.errors.trialDays && (
                                <p className="mt-1 text-sm text-red-600">
                                  {formik.errors.trialDays}
                                </p>
                              )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Usage Limits */}
                    <div className="border-t pt-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Usage Limits
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="allowedTemplateCreation"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Template Creation Limit
                          </label>
                          <input
                            type="number"
                            id="allowedTemplateCreation"
                            name="allowedTemplateCreation"
                            value={formik.values.allowedTemplateCreation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            min="0"
                            className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                              formik.errors.allowedTemplateCreation &&
                              formik.touched.allowedTemplateCreation
                                ? "border-red-500"
                                : "border-gray-300"
                            } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            Set to 0 for unlimited usage
                          </p>
                          {formik.touched.allowedTemplateCreation &&
                            formik.errors.allowedTemplateCreation && (
                              <p className="mt-1 text-sm text-red-600">
                                {formik.errors.allowedTemplateCreation}
                              </p>
                            )}
                        </div>

                        <div>
                          <label
                            htmlFor="allowedSignRequests"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Sign Requests Limit
                          </label>
                          <input
                            type="number"
                            id="allowedSignRequests"
                            name="allowedSignRequests"
                            value={formik.values.allowedSignRequests}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            min="0"
                            className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                              formik.errors.allowedSignRequests &&
                              formik.touched.allowedSignRequests
                                ? "border-red-500"
                                : "border-gray-300"
                            } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            Set to 0 for unlimited usage
                          </p>
                          {formik.touched.allowedSignRequests &&
                            formik.errors.allowedSignRequests && (
                              <p className="mt-1 text-sm text-red-600">
                                {formik.errors.allowedSignRequests}
                              </p>
                            )}
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="allowBulkSend"
                            name="allowBulkSend"
                            checked={formik.values.allowBulkSend}
                            onChange={(e) => {
                              formik.setFieldValue(
                                "allowBulkSend",
                                e.target.checked
                              );
                            }}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label
                            htmlFor="allowBulkSend"
                            className="ml-2 block text-sm text-gray-900"
                          >
                            Allow Bulk Send
                          </label>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Enable bulk send functionality for this plan
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {selectedPlan ? "Update" : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setIsCreateModalOpen(false);
                      setSelectedPlan(null);
                      formik.resetForm();
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;
